import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile } from '../../../types/red'
import { EUR } from '../../../constants/red'
import { redApi } from '../../../services/api'

const TabSimulazione: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile }) => {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [form, setForm] = useState({
    prezzo_acquisto: immobile.prezzo_acquisto ?? '',
    spese_notarili: immobile.totale_spese_notarili ?? '',
    imposte: immobile.totale_imposte ?? '',
    costo_ristrutturazione: '',
    valore_post_ristrutturazione: '',
    mutuo_importo: '',
    mutuo_tasso_annuo: '3.5',
    mutuo_durata_anni: '20',
    mutuo_spese_istruttoria: '1000',
    canone_affitto_mensile: '',
    costo_gestione_annuo: '',
  })

  const handleSimula = async () => {
    const payload = {
      prezzo_acquisto: parseFloat(form.prezzo_acquisto as string) || 0,
      spese_notarili: parseFloat(form.spese_notarili as string) || 0,
      imposte: parseFloat(form.imposte as string) || 0,
      costo_ristrutturazione: parseFloat(form.costo_ristrutturazione as string) || 0,
      valore_post_ristrutturazione: parseFloat(form.valore_post_ristrutturazione as string) || 0,
      mutuo_importo: parseFloat(form.mutuo_importo as string) || 0,
      mutuo_tasso_annuo: parseFloat(form.mutuo_tasso_annuo as string) / 100,
      mutuo_durata_anni: parseInt(form.mutuo_durata_anni as string) || 20,
      mutuo_spese_istruttoria: parseFloat(form.mutuo_spese_istruttoria as string) || 0,
      canone_affitto_mensile: form.canone_affitto_mensile ? parseFloat(form.canone_affitto_mensile as string) : null,
      costo_gestione_annuo: parseFloat(form.costo_gestione_annuo as string) || 0,
    }

    if (!payload.valore_post_ristrutturazione || !payload.mutuo_importo) {
      toast.error('Inserisci almeno valore post-ristrutturazione e importo mutuo')
      return
    }

    setLoading(true)
    try {
      const res = await redApi.simulazione.run(payload)
      setResult(res.data)
    } catch {
      toast.error('Errore nella simulazione')
    } finally {
      setLoading(false)
    }
  }

  const Field = ({ label, field, type = 'text', placeholder }: { label: string; field: string; type?: string; placeholder?: string }) => (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        className="input"
        value={form[field as keyof typeof form]}
        onChange={e => setForm({ ...form, [field]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  )

  return (
    <div className="max-w-4xl space-y-6">
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Parametri Simulazione</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Prezzo acquisto (€)" field="prezzo_acquisto" type="number" />
          <Field label="Spese notarili (€)" field="spese_notarili" type="number" />
          <Field label="Imposte (€)" field="imposte" type="number" />
          <Field label="Costo ristrutturazione (€)" field="costo_ristrutturazione" type="number" />
          <Field label="Valore post-ristrutt. (€) *" field="valore_post_ristrutturazione" type="number" />
          <Field label="Importo mutuo (€) *" field="mutuo_importo" type="number" />
          <Field label="Tasso mutuo (% annuo)" field="mutuo_tasso_annuo" type="number" />
          <Field label="Durata mutuo (anni)" field="mutuo_durata_anni" type="number" />
          <Field label="Spese istruttoria (€)" field="mutuo_spese_istruttoria" type="number" />
          <Field label="Canone affitto mensile (€)" field="canone_affitto_mensile" type="number" />
          <Field label="Costo gestione annuo (€)" field="costo_gestione_annuo" type="number" />
        </div>
        <button onClick={handleSimula} disabled={loading} className="btn-primary mt-4">
          {loading ? 'Calcolo...' : '🔍 Simula'}
        </button>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Confronto */}
          <div className={`p-4 rounded-xl border ${result.convenienza === 'mutuo' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
            <div className="text-lg font-bold">
              {result.convenienza === 'mutuo' ? '💡 Conviene il MUTUO' : '💡 Conviene il CONTANTE'}
            </div>
            <div className="text-sm text-gray-600">
              Risparmio netto stimato: <strong>{EUR.format(result.risparmio_netto)}</strong>
              {result.break_even_anni && ` · Break-even: ${result.break_even_anni} anni`}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Scenario Contante */}
            <div className="card p-5">
              <h3 className="text-base font-semibold text-gray-800 mb-3">💰 Scenario Contante</h3>
              <div className="space-y-2 text-sm">
                <Row label="Investimento totale" value={EUR.format(result.investimento_totale_contante)} />
                <Row label="Ricavo vendita" value={EUR.format(result.ricavo_vendita_contante)} />
                <Row label="Utile lordo" value={EUR.format(result.utile_lordo_contante)} color={result.utile_lordo_contante >= 0 ? 'text-green-600' : 'text-red-600'} />
                <Row label="ROI" value={`${result.roi_contante_percento}%`} />
              </div>
            </div>

            {/* Scenario Mutuo */}
            <div className="card p-5">
              <h3 className="text-base font-semibold text-gray-800 mb-3">🏦 Scenario Mutuo</h3>
              <div className="space-y-2 text-sm">
                <Row label="Investimento iniziale" value={EUR.format(result.investimento_iniziale_mutuo)} />
                <Row label="Rata mensile" value={EUR.format(result.rata_mensile)} />
                <Row label="Interessi totali" value={EUR.format(result.interessi_totali)} />
                <Row label="Costo totale" value={EUR.format(result.costo_totale_mutuo)} />
                <Row label="Utile lordo" value={EUR.format(result.utile_lordo_mutuo)} color={result.utile_lordo_mutuo >= 0 ? 'text-green-600' : 'text-red-600'} />
                <Row label="ROI" value={`${result.roi_mutuo_percento}%`} />
                {result.cashflow_mensile !== null && (
                  <Row label="Cashflow mensile" value={EUR.format(result.cashflow_mensile)} color={result.cashflow_mensile >= 0 ? 'text-green-600' : 'text-red-600'} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const Row = ({ label, value, color = 'text-gray-900' }: { label: string; value: string; color?: string }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={`font-semibold ${color}`}>{value}</span>
  </div>
)

export default TabSimulazione
